/**
 * Feature registry — the single source of truth for every gateable feature
 * (B2B Phase A, #1644). Replaces the scattered gating (PostHog flags, VITE_FF_*,
 * subscription tiers, role permissions, FIRM_BLOCKED_CLIENT_ROUTES) with one
 * declarative map. Nothing is ever deleted — features are gated OFF.
 *
 * Resolution precedence is in ./resolve.ts. Each feature declares its default
 * per EXPERIENCE PRESET ('firm' vs 'individual') so one switch flips the whole
 * product. See docs/plans/2026-08-b2b-product-reconciliation.md §3.
 */

import type { Tier } from '@/hooks/useSubscription';

export type FeatureAudience = 'firm' | 'individual' | 'both' | 'infra';
/**
 * Experience presets. `collector` (Disclosure Collector v1, M1 #1747) is the
 * consumer disclosure-collection product: only the collection spine is ON.
 */
export type FeaturePreset = 'firm' | 'individual' | 'collector';

export interface FeatureDef {
  /** Stable key, snake_case. Env override: VITE_FF_FEATURE_<UPPER_KEY>. */
  key: string;
  title: string;
  category: string;
  audience: FeatureAudience;
  /** Default on/off per experience preset. */
  defaultOn: Record<FeaturePreset, boolean>;
  /** Hard tier requirement applied on top of everything (AND). */
  minTier?: Tier;
  /** Optional PostHog flag that can override the preset default. */
  posthogFlag?: string;
  /** Client route(s) this feature gates (for nav/route integration). */
  routes?: string[];
}

const def = (
  key: string,
  title: string,
  category: string,
  audience: FeatureAudience,
  firm: boolean,
  individual: boolean,
  extra: Partial<Omit<FeatureDef, 'defaultOn'>> & { collector?: boolean } = {},
): FeatureDef => {
  const { collector = false, ...rest } = extra;
  return { key, title, category, audience, defaultOn: { firm, individual, collector }, ...rest };
};

/**
 * The registry. `firm` defaults encode the B2B-first experience: individual /
 * over-scoped features are OFF for firms (kept in code, reachable via flag or
 * the individual preset). Core discovery/disclosure/intake features are ON.
 */
export const FEATURE_LIST: FeatureDef[] = [
  // ---- CORE (firm-critical, on for both) ----
  def('documents', 'Documents', 'intake', 'both', true, true, { routes: ['/documents', '/case-library'], collector: true }),
  def('correspondence', 'Correspondence', 'intake', 'both', true, true, { routes: ['/correspondence'] }),
  def('case_intelligence', 'Case Intelligence', 'intake', 'both', true, true, { routes: ['/case-intelligence'] }),
  // DL1 (#1834): the collector client reaches readiness and discovery by URL; the nav stays three items (navConfig).
  def('case_readiness', 'Case Readiness (what\'s missing)', 'discovery', 'firm', true, true, { routes: ['/case-readiness'], collector: true }),
  def('discovery', 'Discovery (RFP & Interrogatories)', 'discovery', 'firm', true, true, { routes: ['/discovery'], collector: true }),
  def('discovery_inbox', 'Discovery Inbox', 'discovery', 'firm', true, true, { routes: ['/discovery-inbox'] }),
  def('export', 'Court-ready Export', 'output', 'both', true, true, { routes: ['/export'], collector: true }),
  // #1714: forensic report UI is deferred to v1.1 (#932/#851/#46) and the page is
  // a CTA-less dead-end (needs an AI container the case lacks). Hide it for both
  // presets until the real UI ships — flip these back on then.
  def('forensic_report', 'Forensic Report', 'output', 'both', false, false, { routes: ['/forensic-report'] }),
  def('support_calculator', 'Support Calculator', 'financial', 'both', true, true, { routes: ['/support-calculator'] }),
  def('ask_sage', 'Sage Chat', 'ai', 'both', true, true, { routes: ['/ask-sage'] }),
  def('legal_team', 'Legal Team (Case Org)', 'ai', 'both', true, true, { routes: ['/legal-team'] }),
  def('financials', 'Financial Disclosure', 'financial', 'both', true, true, { routes: ['/financials'] }),
  def('tasks', 'Tasks', 'intake', 'both', true, true, { routes: ['/tasks'], collector: true }),
  // ---- Disclosure Collector v1 (#1745): the one page of the collector product ----
  def('collection', 'Collection (disclosure spine)', 'collector', 'both', true, true, { routes: ['/collection'], collector: true }),

  // ---- Advanced manual financial entry (the 22 per-category forms). Firms
  //      lead with document-dump-first (the /financials hub + upload); the
  //      cumbersome detail forms are Advanced (off for firms, kept in code).
  def('advanced_financial_entry', 'Advanced: manual financial entry', 'financial', 'individual', false, true, {
    routes: [
      '/financials/business', '/financials/real-estate', '/financials/vehicles',
      '/financials/bank-accounts', '/financials/credit-cards', '/financials/investments',
      '/financials/retirement', '/financials/life-insurance', '/financials/health-insurance',
      '/financials/pc-insurance', '/financials/tax-returns', '/financials/third-party-payments',
      '/financials/crypto', '/financials/pay-stubs', '/financials/ssa-statement',
      '/financials/promissory-notes', '/financials/leases', '/financials/credit-report',
      '/financials/mortgages', '/financials/contingent-liabilities',
    ],
  }),

  // ---- FLAG-OFF for firms (individual / over-scoped; kept, default off) ----
  def('strategy', 'AI Settlement Strategy', 'strategy', 'individual', false, true, { routes: ['/strategy'] }),
  def('goals', 'Settlement Goals', 'strategy', 'individual', false, true, { routes: ['/goals'] }),
  def('redline', 'Redline', 'negotiation', 'individual', false, true, { routes: ['/redline'] }),
  def('settlement_wizard', 'Settlement Wizard', 'negotiation', 'individual', false, true, { routes: ['/settlement-wizard'] }),
  def('sibyl', 'Sibyl Negotiation AI', 'negotiation', 'individual', false, true, { routes: ['/sibyl'] }),
  def('asset_emotions', 'Asset Emotions', 'psychology', 'individual', false, true, { routes: ['/asset-emotions'] }),
  def('know_your_spouse', 'Know Your Spouse', 'psychology', 'individual', false, true, { routes: ['/know-your-spouse'] }),
  def('session_debrief', 'Session Debrief', 'psychology', 'individual', false, true, { routes: ['/session-debrief'] }),
  def('entity_planning', 'Entity Planning', 'financial', 'individual', false, true, { routes: ['/entity-planning'] }),
  def('learn', 'Learn (Education)', 'education', 'individual', false, true, { routes: ['/learn'] }),
];

export const FEATURES: Record<string, FeatureDef> = Object.freeze(
  Object.fromEntries(FEATURE_LIST.map((f) => [f.key, f])),
);

export type FeatureKey = string;

/**
 * The registered feature that gates a route, or undefined. When routes nest
 * (e.g. /financials vs /financials/retirement), the MOST SPECIFIC (longest)
 * matching route wins — so a sub-page can be gated separately from its hub.
 */
export function featureForRoute(pathname: string): FeatureDef | undefined {
  const path = pathname.replace(/^\/(en|es|zh)(?=\/)/, '');
  let best: FeatureDef | undefined;
  let bestLen = -1;
  for (const f of FEATURE_LIST) {
    for (const r of f.routes ?? []) {
      if ((path === r || path.startsWith(`${r}/`)) && r.length > bestLen) {
        best = f;
        bestLen = r.length;
      }
    }
  }
  return best;
}
